// services/bonusService.js
// Service for awarding and checking bonuses
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';

// Bonus types that can be awarded
export const BONUS_TYPES = {
  PROFILE_COMPLETION: 'PROFILE_COMPLETION',
  TOUR_COMPLETION: 'TOUR_COMPLETION',
  TOUR_WELCOME: 'TOUR_WELCOME',
  TOUR_EARNER: 'TOUR_EARNER',
  TOUR_VENDOR: 'TOUR_VENDOR',
};

// Token amounts for each bonus
export const BONUS_AMOUNTS = {
  PROFILE_COMPLETION: 20,
  TOUR_COMPLETION: 10,
  TOUR_WELCOME: 10,
  TOUR_EARNER: 15,
  TOUR_VENDOR: 20,
};

/**
 * Award a bonus to the current user
 * @param {string} bonusType - Type of bonus (from BONUS_TYPES)
 * @param {object} metadata - Optional metadata (e.g., tourName)
 * @returns {Promise<{success: boolean, tokensAwarded?: number, newBalance?: number, error?: string}>}
 */
export const awardBonus = async (bonusType, metadata = {}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }
    
    const response = await fetch(`${API_BASE_URL}/auth/award-bonus`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ bonusType, metadata })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      // Don't treat "already claimed" as an error - it's expected
      if (data.code === 'BONUS_ALREADY_CLAIMED') {
        return { success: false, alreadyClaimed: true, error: data.error };
      }
      return { success: false, error: data.error || 'Failed to award bonus' };
    }
    
    return {
      success: true,
      tokensAwarded: data.tokensAwarded,
      newBalance: data.newBalance,
      message: data.message
    };
    
  } catch (error) {
    console.error('Error awarding bonus:', error);
    return { success: false, error: error.message || 'Network error' };
  }
};

/**
 * Check which bonuses have been claimed
 * @returns {Promise<{success: boolean, bonuses?: object, tokenBalance?: number, error?: string}>}
 */
export const getBonusStatus = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }
    
    const response = await fetch(`${API_BASE_URL}/auth/bonus-status`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to fetch bonus status' };
    }
    
    return {
      success: true,
      bonuses: data.bonuses,
      tokenBalance: data.tokenBalance
    };
    
  } catch (error) {
    console.error('Error fetching bonus status:', error);
    return { success: false, error: error.message || 'Network error' };
  }
};

/**
 * Award profile completion bonus
 * Call this when user completes their profile
 */
export const awardProfileCompletionBonus = async () => {
  return awardBonus(BONUS_TYPES.PROFILE_COMPLETION);
};

/**
 * Award tour completion bonus
 * @param {string} tourName - Name of the completed tour
 */
export const awardTourCompletionBonus = async (tourName) => {
  // Map tour names to bonus types
  const tourBonusMap = {
    'newMember': BONUS_TYPES.TOUR_WELCOME,
    'Welcome Tour': BONUS_TYPES.TOUR_WELCOME,
    'earner': BONUS_TYPES.TOUR_EARNER,
    'Income Maximizer': BONUS_TYPES.TOUR_EARNER,
    'vendorSuccess': BONUS_TYPES.TOUR_VENDOR,
    'Vendor Success Tour': BONUS_TYPES.TOUR_VENDOR,
    'productListing': BONUS_TYPES.TOUR_VENDOR,
  };
  
  const bonusType = tourBonusMap[tourName] || BONUS_TYPES.TOUR_COMPLETION;
  
  return awardBonus(bonusType, { tourName });
};

/**
 * Check if profile is complete enough for bonus
 * @param {object} user - User object
 * @returns {boolean}
 */
export const isProfileComplete = (user) => {
  if (!user) return false;
  
  const profile = user.profile || user;
  
  // Required fields for "complete" profile
  const hasName = !!profile.name;
  const hasEmail = !!profile.email;
  const hasPhone = !!profile.phone;
  const hasLocation = !!(profile.location?.zipCode || profile.location?.city || profile.zipCode);
  
  // Need at least 3 of 4 fields filled
  const filledFields = [hasName, hasEmail, hasPhone, hasLocation].filter(Boolean).length;
  
  return filledFields >= 3;
};

export default {
  BONUS_TYPES,
  BONUS_AMOUNTS,
  awardBonus,
  getBonusStatus,
  awardProfileCompletionBonus,
  awardTourCompletionBonus,
  isProfileComplete,
};